import { Box, Link } from '@mui/material';
import TreeItemGroup from '../TreeItemGroup';
import { NavigableTreeNode } from '../../types';
import useTreeContext from '../../context/useTreeContext';
import TreeItemExpandButton from './TreeItemExpandButton';

function isInCurrentTreeNodePath(
  node: NavigableTreeNode,
  currentTreeNodePath: NavigableTreeNode[],
): boolean {
  return currentTreeNodePath.some(
    (pathNode) => pathNode.nodeId === node.nodeId,
  );
}

export default function TreeItem({
  node,
  level,
}: Readonly<{
  node: NavigableTreeNode;
  level: number;
}>) {
  const { handleKeyDown, onSelectNode, currentTreeNode, currentTreeNodePath } =
    useTreeContext();

  return (
    <li role='none'>
      <Link
        component='button'
        underline='hover'
        variant='body1'
        color='text.primary'
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          paddingY: 0.5,
          paddingLeft: level * 2 + (node.hasChildren ? 0 : 5.25),
          textAlign: 'left',
          ...(isInCurrentTreeNodePath(node, currentTreeNodePath) && {
            fontWeight: 700,
          }),
        }}
        role='treeitem'
        tabIndex={currentTreeNode?.nodeId === node.nodeId ? 0 : -1}
        data-node-id={node.nodeId}
        aria-level={node.level}
        aria-setsize={node.setSize}
        aria-posinset={node.posInSet}
        aria-expanded={node.hasChildren ? node.isExpanded : undefined}
        aria-selected={currentTreeNode?.nodeId === node.nodeId}
        onClick={() => {
          onSelectNode(node.nodeId);
        }}
        onKeyDown={(e) => handleKeyDown(e, node)}
      >
        {node.hasChildren && <TreeItemExpandButton node={node} />}
        <Box component='span'>{node.title}</Box>
      </Link>
      {node.hasChildren && node.isExpanded && node.children && (
        <TreeItemGroup nodes={node.children} level={level + 1} />
      )}
    </li>
  );
}
